// =====================================================================
// MisAnalysisAskBox.jsx
// =====================================================================
// Cost-forensics AI scoped to a single property's MIS Analysis data.
// Extracted from MisExpenses.jsx (iter-35e).
// =====================================================================

import { useState } from "react";
import { Loader2, Sparkles, Send } from "lucide-react";
import { Input } from "../../components/ui/input";
import { api } from "../../lib/api";
import { logError } from "../../lib/log";

export default function MisAnalysisAskBox({ location }) {
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const ask = async () => {
    const question = q.trim();
    if (!question) return;
    setLoading(true); setError(null); setAnswer(null);
    try {
      const { data } = await api.post("/mis-analysis/ask", { question, location });
      setAnswer(data?.answer || "No answer.");
    } catch (e) {
      logError("mis-analysis/ask failed", e);
      setError(e?.response?.data?.detail || "AI unavailable. Try again.");
    } finally { setLoading(false); }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) ask();
  };

  return (
    <section className="bg-white border border-teal-300 rounded-xl p-5" data-testid="mis-analysis-ask">
      <header className="mb-2 flex items-center gap-2 flex-wrap">
        <Sparkles className="w-3.5 h-3.5 text-teal-700" />
        <span className="font-display font-bold text-[13px] text-teal-900">Ask MIS Analysis · AI</span>
        <span className="text-[10.5px] text-teal-700">scoped to {location} · cost forensics & variance</span>
      </header>
      <div className="flex gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
          placeholder={`e.g. "Why is utility up MoM at ${location}?" or "Which line items breached AOP?"`}
          className="h-9 rounded-md border-teal-200 text-[12px]"
          data-testid="mis-analysis-ask-input"
          disabled={loading}
        />
        <button
          onClick={ask}
          disabled={loading || !q.trim()}
          data-testid="mis-analysis-ask-submit"
          className="text-[11px] font-bold bg-teal-700 text-white px-3 rounded hover:bg-teal-800 disabled:opacity-60 inline-flex items-center gap-1"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Ask
        </button>
      </div>
      {error && (
        <div className="mt-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5" data-testid="mis-analysis-ask-error">
          {error}
        </div>
      )}
      {answer && !error && (
        <div className="mt-2 text-[12px] leading-relaxed text-zinc-800 bg-teal-50/60 border border-teal-200 rounded px-3 py-2 whitespace-pre-wrap" data-testid="mis-analysis-ask-answer">
          {answer}
        </div>
      )}
    </section>
  );
}
